import { useTranslation } from "react-i18next";
import { useInfiniteQuery, useQuery, useQueryClient } from "react-query";
import { useRecoilValue } from "recoil";
import _NotificationsService, { NotiFilterAtom } from "services/notifications-services";
import { useCallback, useEffect } from "react";
import { useAuthContext } from "store/auth";
import * as signalR from "@microsoft/signalr";
import { handleRefreshToken } from "helpers/client";

const NOTIFICATIONS_URL = process.env.REACT_APP_NOTIFICATIONS_URL + "/NotificationHub";

export function useNotifications() {
  const { t } = useTranslation();
  const filters = useRecoilValue(NotiFilterAtom);
  const [{ user }] = useAuthContext();
  const queryClient = useQueryClient();
  const pageSize = 10;

  const joinRoom = useCallback(async () => {
    try {
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(NOTIFICATIONS_URL, {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets
        })
        .withAutomaticReconnect()
        .build();

      connection.on("NotificationChannel", () => {
        queryClient.invalidateQueries(["notifications"]);
        queryClient.invalidateQueries(["notifications-length"]);
      });

      connection.on("RefreshTokenChannel", () => {
        handleRefreshToken().then(
          () => window.location.reload()
        );
      });

      await connection.start();
      await connection.invoke("Register", { userId: user?.Id, userType: 1 });
    } catch (e) {
      console.log("Error From Notifications ::: ", e);
    }
  }, [queryClient, user]);

  useEffect(() => {
    joinRoom();
  }, [joinRoom]);

  const { data, ...result } = useInfiniteQuery(
    ["notifications", t, filters],
    async ({ pageParam = 0 }) => {
      const params = {
        skipCount: pageParam,
        maxResultCount: pageSize,
        sortingDirection: "DESC",
        filter: filters.filter,
        searchTerm: filters.searchTerm
      };

      const { response } = await _NotificationsService.getAllNotifications(params);
      if (response) return response;
    },
    {
      enabled: !!filters,
      getNextPageParam: (lastPage: any, allPages) => {
        const numberOfPages = lastPage ? lastPage.totalCount / pageSize : undefined;
        if (!numberOfPages) return undefined;
        return numberOfPages > allPages.length ? allPages.length + 1 : undefined;
      }
    }
  );

  return {
    ...result,
    notiList: data?.pages ?? {},
    totalCount: data?.pages?.[0]?.totalCount ?? 0
  };
}

export function useNotificationsLength() {
  const { t } = useTranslation();

  const { isLoading, isError, isSuccess, ...result } = useQuery(["notifications-length", t], async () => {
    const { response } = await _NotificationsService.getNotificationsLength();
    if (response) return response;
  });
  return {
    ...result,
    notiLength: result?.data,
    isLoading,
    isError,
    isSuccess
  };
}